import React from 'react';
import { userData, projects } from '../../data';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const Archive = ({ onNavigate }) => {
    useDocumentTitle('Archive | Jerel Layog');

    return(
    <section className="content-pane w-full h-full flex flex-col pt-16 md:pt-32 2xl:pt-48 overflow-y-auto custom-scrollbar px-2 sm:px-4 2xl:px-6 relative z-20" tabIndex={-1} aria-labelledby="heading-archive">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 2xl:gap-6 mb-6 md:mb-10 2xl:mb-14 ml-2 2xl:ml-4 relative z-20">
            <button
                onClick={() => onNavigate("PROJECTS")}
                className="bg-black text-[#FFF200] font-bebas text-xl md:text-3xl 2xl:text-4xl px-4 2xl:px-6 py-2 2xl:py-3 border-2 md:border-4 2xl:border-[6px] border-white transform hover:-translate-x-2 2xl:hover:-translate-x-3 transition-transform shadow-[4px_4px_0_#FFF200] 2xl:shadow-[6px_6px_0_#FFF200] focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
            >
                ◄ BACK TO PROJECTS
            </button>
            <h2 id="heading-archive" className="text-4xl sm:text-5xl md:text-7xl 2xl:text-8xl font-bebas text-white bg-black px-4 2xl:px-6 py-2 2xl:py-3 border-4 md:border-[6px] 2xl:border-[8px] border-white shadow-[6px_6px_0_#000] md:shadow-[12px_12px_0_#000] 2xl:shadow-[18px_18px_0_#000] transform md:-rotate-2 2xl:-rotate-3 inline-block w-fit m-0">
                PROJECT ARCHIVE
            </h2>
        </div>
        
        <div className="flex flex-col gap-6 md:gap-10 2xl:gap-14 max-w-full pb-24 md:pb-32 2xl:pb-40 pr-2 sm:pr-4 2xl:pr-6 pl-2 2xl:pl-4 pt-2 2xl:pt-4">
            {userData.archive.map((item, idx) => (
                <article key={idx} className="relative group focus-within:scale-[1.01] hover:scale-[1.01] transition-transform">
                    <div className="absolute inset-0 bg-[#A30000] transform translate-x-1 translate-y-1 md:translate-x-2 md:translate-y-2 2xl:translate-x-3 2xl:translate-y-3 border-2 md:border-4 2xl:border-[6px] border-black" aria-hidden="true"></div>
                    
                    <div className="relative bg-[#111111] border-l-2 md:border-l-4 2xl:border-l-[6px] border-[#f5c800] p-4 md:p-6 2xl:p-8 flex flex-col lg:flex-row justify-between items-start lg:items-center shadow-[inset_0_0_10px_rgba(255,0,0,0.2)] gap-6 lg:gap-8 2xl:gap-10 group-hover:border-4 2xl:group-hover:border-[6px] border-[#f5c800] ">
                        
                        {/* LEFT COLUMN: Year, Title, Company */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 w-full lg:w-auto">
                            <span className="font-bebas text-2xl md:text-4xl 2xl:text-5xl text-[#FF5555] shrink-0">{item.year}</span>
                            <div>
                                <h3 className="text-xl md:text-2xl 2xl:text-3xl font-black italic text-white uppercase tracking-tight group-hover:text-[#FFF200] transition-colors">{item.title}</h3>
                                <p className="font-montserrat text-xs md:text-sm 2xl:text-base text-gray-300 font-medium mt-1 2xl:mt-2">{item.company}</p>
                            </div>
                        </div>
                        
                        {/* RIGHT COLUMNS: Tags + Action Link */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start lg:justify-end gap-4 sm:gap-6 w-full lg:w-auto lg:flex-1 mt-2 lg:mt-0">
                            
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1.5 md:gap-2 2xl:gap-3 justify-start lg:justify-end lg:flex-1">
                                {item.tags.map(tag => (
                                    <span key={tag} className="text-[10px] md:text-xs 2xl:text-sm font-bold bg-white text-black px-1.5 md:px-2 2xl:px-3 py-0.5 md:py-1 2xl:py-1.5 transform md:-rotate-2 2xl:-rotate-3 border border-white group-hover:bg-[#A30000] group-hover:text-white transition-colors">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            
                            {/* Link Column Button */}
                            <div className="shrink-0 flex justify-start sm:w-28 lg:w-32 lg:justify-end">
                                {item.link ? (
                                    item.link.startsWith('showcase') ? (
                                        <button
                                            onClick={() => {
                                                const proj = projects.find(p => p.link === item.link);
                                                if (proj) onNavigate('PROJECT_DETAIL', {
                                                    name: proj.title,
                                                    image: proj.imgSrc,
                                                    tags: proj.tags,
                                                    desc: proj.description,
                                                    link: proj.link,
                                                    slug: proj.slug,
                                                    role: proj.role,
                                                    timeline: proj.timeline,
                                                });
                                            }}
                                            className="font-bebas text-xl md:text-2xl 2xl:text-3xl bg-transparent text-white px-4 2xl:px-5 py-1.5 2xl:py-2.5 border-2 md:border-4 2xl:border-[6px] border-white transform md:rotate-2 2xl:rotate-3 hover:bg-[#f5c800] hover:text-black hover:-translate-y-1 2xl:hover:-translate-y-2 transition-all shadow-[4px_4px_0_#A30000] 2xl:shadow-[6px_6px_0_#A30000] focus:outline-none focus-visible:ring-4 focus-visible:ring-white w-full sm:w-auto text-center"
                                            aria-label={`View project: ${item.title}`}
                                        >
                                            VIEW ↗
                                        </button>
                                    ) : (
                                        <a
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-bebas text-xl md:text-2xl 2xl:text-3xl bg-transparent text-white px-4 2xl:px-5 py-1.5 2xl:py-2.5 border-2 md:border-4 2xl:border-[6px] border-white transform md:rotate-2 2xl:rotate-3 hover:bg-[#f5c800] hover:text-black hover:-translate-y-1 2xl:hover:-translate-y-2 transition-all shadow-[4px_4px_0_#A30000] 2xl:shadow-[6px_6px_0_#A30000] focus:outline-none focus-visible:ring-4 focus-visible:ring-white w-full sm:w-auto text-center"
                                            aria-label={`View project: ${item.title}`}
                                        >
                                            VIEW ↗
                                        </a>
                                    )
                                ) : (
                                    <div className="hidden sm:block w-full" aria-hidden="true"></div>
                                )}
                            </div>

                        </div>

                    </div>
                </article>
            ))}
        </div>
    </section>
    );
};

export default Archive;